/**
 * A descriptor card for forms whose every box is owned by an existing editor.
 *
 * The 208's safety message and the 215A's hazard rows live in their own
 * entities with their own validation and their own saves. Rendering them
 * through the generic controls would lose that, so the card contributes only
 * what was missing: the printed box numbers, titles, and order.
 */

import type React from 'react';
import { useMemo, type ReactNode } from 'react';
import { useScopedIncident } from '../hooks/incidentHooks';

import { useOperationalPeriodWindow } from '../hooks/useOperationalPeriodWindow';
import { DescriptorCard } from './DescriptorCard';
import FormVersioningBanner from './FormVersioningBanner';

export type FemaSectionShellProps = {
  baseId: string;
  /** Existing editors, keyed by the descriptor source whose box they fill. */
  slots: Record<string, ReactNode>;
  /** Boxes a slotted editor already covers, so they are not shown twice. */
  omit?: string[];
  banner?: ReactNode;
  headerActions?: ReactNode;
};

export const FemaSectionShell: React.FC<FemaSectionShellProps> = ({
  baseId,
  slots,
  omit,
  banner,
  headerActions,
}) => {
  const incident = useScopedIncident();
  const { range: operationalPeriod } = useOperationalPeriodWindow();
  // Nothing here takes part in a card-level save, so the form has no values.
  const values = useMemo(() => ({}), []);

  return (
    <DescriptorCard
      baseId={baseId}
      family="fema"
      values={values}
      onSave={() => undefined}
      banner={
        <FormVersioningBanner baseId={baseId}>{banner}</FormVersioningBanner>
      }
      headerActions={headerActions}
      autoValues={{ incidentName: incident?.name, operationalPeriod }}
      slots={slots}
      omit={omit}
    />
  );
};

export default FemaSectionShell;
